import type { Point2d } from "@logic/lib/2d"
import { boxToMatrix, isSamePoint } from "@logic/lib/2d"
import { studioToScreen } from "@logic/lib/design"
import { useRecoilTransaction_UNSTABLE } from "recoil"

import IS_DEV from "~/utils/isDev"

import { getDragBox } from "."
import { getMousePositionOnScreen } from ".."
import type { TransactionOperation } from ".."
import { startDrag } from "./startDrag"
import { startPointState } from "../../../inputs"
import { getTool } from "../../../tool"
import { zoomState } from "../../../view"
import { panelGapState } from "../../panels"
import { divergeTimeline } from "../../timeline/transactions"
import { addPanel } from "../addPanel"
import { clickCreatePanel } from "../clickCreatePanel"
import { getPanelScale } from "../getPanelScale"

export type MakePanelOriginMatrixOptions = {
  orientation: `landscape` | `portrait`
  isPreview?: boolean
}

export const makePanelOriginMatrix: TransactionOperation<
  MakePanelOriginMatrixOptions,
  Point2d[]
> = (transactors, { orientation, isPreview = false }) => {
  // disable drag to create panels in production
  if (!IS_DEV) return []
  const { get } = transactors
  const dragBox = getDragBox(transactors)
  if (!dragBox) return []
  const zoom = get(zoomState)
  const gap = get(panelGapState)
  const panelScale = getPanelScale(transactors, null)
  const { h, w } =
    orientation === `portrait`
      ? panelScale
      : { h: panelScale.w, w: panelScale.h }
  const panel = studioToScreen.point({ x: w, y: h }, zoom)
  const offset = studioToScreen.point({ x: w, y: h }, zoom)
  const points = boxToMatrix(
    orientation === `landscape` && !isPreview
      ? { ...dragBox, w: dragBox.w + panel.x }
      : dragBox,
    offset,
    dragBox.quadrant,
    orientation === `landscape` && !isPreview
      ? studioToScreen.point({ x: w, y: 0 }, zoom)
      : undefined,
  )

  return points
}

export const finishDragCreate: TransactionOperation = (transactors) => {
  const { get, set } = transactors
  const mouse = getMousePositionOnScreen(transactors)
  const startPoint = get(startPointState)
  const isDragging = startPoint && !isSamePoint(startPoint, mouse)
  if (!isDragging) {
    clickCreatePanel(transactors)
    set(startPointState, null)

    return
  }
  const zoom = get(zoomState)
  const tool = getTool(transactors)
  const orientation = tool === `PanelCreatorPortrait` ? `portrait` : `landscape`
  const points = makePanelOriginMatrix(transactors, { orientation })
  const { w, h } = studioToScreen.scale(getPanelScale(transactors, null), zoom)

  const changes = points
    .map((point) =>
      addPanel(transactors, {
        ...point,
        w,
        h,
        r: orientation === `portrait` ? 0 : 90,
        orientation: orientation === `portrait` ? 0 : 90,
      }),
    )
    .flat()
  if (changes.length) divergeTimeline(transactors, changes)

  set(startPointState, null)
}

export const useDragCreate: () => [VoidFunction, VoidFunction] = () => [
  useRecoilTransaction_UNSTABLE((transactors) => () => startDrag(transactors)),
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => finishDragCreate(transactors),
  ),
]
